// @tg-pattern: UNIVERSAL_RENDERER
// @architect-approved: EXPERIMENTAL_DEV - Two-Mode Navigation MCMI integration (2026-07-20)
/**
 * Universal Renderer - GWALL Glass Wall Client
 *
 * Renders ANY component based on SQL-generated definition
 * Zero hardcoded business logic - SQL controls everything
 *
 * Usage:
 *   <UniversalRenderer seq={10886} />
 *
 * SQL returns complete definition:
 *   - Fields to display (name, type, label)
 *   - Buttons to show (label, icon, action)
 *   - Data to populate (from database)
 *   - Layout hints (mobile, desktop, watch, TV)
 *
 * This component just renders what SQL sends - Netflix + Citrix baby!
 *
 * Created: 2026-07-20
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
    Paper,
    Alert,
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { apiRequest } from '../utils/apiService';
import { getTsxFileName } from '../utils/componentIdentity';
import TORGPROFILE_v1_simple from '../TGC/PictureFrames/T-ORG-PROFILE.v1-simple';

interface Field {
    name: string;
    type: string;
    label: string;
    width?: number;
    rowGroup?: number;
    required?: boolean;
    defaultValue?: any;
    placeholder?: string;
    helpText?: string;
}

// Picture Frame dispatch table — GWALL.Picture_Frame_Registry.Frame_Code -> composite
// frame component. Resolved server-side into `component.template` (see
// GWALL.sp_Universal_Action_Handler: COALESCE(Web_Picture_Frame_Code, Tab_Outlet_Pattern)).
// Unmatched/legacy templates fall through to the generic field-loop render below.
const FRAME_COMPONENTS: Record<string, React.FC<any>> = {
    'T-ORG-PROFILE': TORGPROFILE_v1_simple,
};

interface ButtonDef {
    id: number;
    label: string;
    icon?: string;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    variant?: 'contained' | 'outlined' | 'text';
    action: string;
    mcmiClickId?: number;
    targetSeq?: number;
    requiresSelection?: boolean;
    requiresConfirmation?: boolean;
    confirmationMessage?: string;
}

interface ComponentDefinition {
    seq: number;
    title: string;
    template: string;
    layoutHint?: string;
    fields: Field[];
    buttons: ButtonDef[];
    data: any;
}

interface UniversalRendererProps {
    seq: number;
}

const Self: React.FC<UniversalRendererProps> = ({ seq }) => {
    const [component, setComponent] = useState<ComponentDefinition | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadComponent = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const result = (await apiRequest('/api/exec', 'POST', {
                action_type: 'LOAD_COMPONENT',
                component_seq: seq,
                device_type: 'web',
                screen_width: window.innerWidth,
                screen_height: window.innerHeight,
            })) as any;

            if (result.status === 'success') {
                setComponent(result.component);
            } else {
                setError('Failed to load component');
            }
        } catch (err: any) {
            setError(err.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [seq]);

    useEffect(() => {
        loadComponent();
    }, [loadComponent]);

    const handleButtonClick = async (button: ButtonDef) => {
        // Show confirmation if required
        if (button.requiresConfirmation) {
            const confirmed = window.confirm(
                button.confirmationMessage || `Are you sure you want to ${button.label}?`,
            );
            if (!confirmed) return;
        }

        setLoading(true);

        try {
            const result = (await apiRequest('/api/exec', 'POST', {
                action_type: 'BUTTON_CLICK',
                component_seq: seq,
                button_id: button.id,
                device_type: 'web',
            })) as any;

            if (result.status === 'success') {
                // SQL returns updated component state
                setComponent(result.component);
            }
        } catch (err: any) {
            setError(err.message || 'Button action failed');
        } finally {
            setLoading(false);
        }
    };

    const renderField = (field: Field) => {
        const value = component?.data?.[field.name];

        switch (field.type) {
            case 'boolean':
                return (
                    <FormControlLabel
                        key={field.name}
                        control={
                            <Switch
                                checked={value === 1 || value === true}
                                disabled // Read-only for MVP
                            />
                        }
                        label={field.label}
                    />
                );

            case 'text':
            case 'number':
            case 'email':
            case 'date':
                return (
                    <TextField
                        key={field.name}
                        label={field.label}
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={value || ''}
                        fullWidth
                        margin="normal"
                        required={field.required}
                        placeholder={field.placeholder}
                        helperText={field.helpText}
                        disabled // Read-only for MVP
                    />
                );

            default:
                return (
                    <Typography key={field.name}>
                        {field.label}: {String(value)}
                    </Typography>
                );
        }
    };

    const renderButton = (button: ButtonDef) => {
        // Dynamically resolve MUI icon
        const IconComponent = button.icon ? (Icons as any)[button.icon] : null;

        return (
            <Button
                key={button.id}
                variant={button.variant || 'contained'}
                color={button.color || 'primary'}
                onClick={() => handleButtonClick(button)}
                startIcon={IconComponent ? <IconComponent /> : null}
                disabled={loading}
            >
                {button.label}
            </Button>
        );
    };

    // ═══════════════════════════════════════════════════════════
    // LOADING STATE
    // ═══════════════════════════════════════════════════════════
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════════════
    // ERROR STATE
    // ═══════════════════════════════════════════════════════════
    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════════════
    // NO COMPONENT
    // ═══════════════════════════════════════════════════════════
    if (!component) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning">Component {seq} not found</Alert>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════════════
    // RENDER COMPONENT (SQL-Driven)
    // ═══════════════════════════════════════════════════════════
    const FrameComponent = component.template ? FRAME_COMPONENTS[component.template] : undefined;

    return (
        <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 2 }}>
            {FrameComponent ? (
                <FrameComponent
                    title={component.title}
                    fields={component.fields}
                    buttons={component.buttons}
                    data={component.data}
                    loading={loading}
                    onButtonClick={handleButtonClick}
                />
            ) : (
                <>
                    {/* Title */}
                    <Typography variant="h5" sx={{ mb: 3 }}>
                        {component.title}
                    </Typography>

                    {/* Fields (SQL defines structure) */}
                    <Box sx={{ mb: 3 }}>{component.fields?.map(renderField)}</Box>

                    {/* Buttons (SQL defines actions) */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {component.buttons?.map(renderButton)}
                    </Box>
                </>
            )}

            {/* Debug Info (remove in production) */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    🎯 GWALL Universal Renderer
                    <br />
                    Seq: {component.seq} | Template: {component.template} | Layout:{' '}
                    {component.layoutHint}
                    <br />
                    Fields: {component.fields?.length || 0} | Buttons:{' '}
                    {component.buttons?.length || 0}
                </Typography>
            </Box>
        </Paper>
    );
};

Self.displayName = getTsxFileName(import.meta.url);

export default Self;
